import { LoadingButton } from '@mui/lab';
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { Routes } from 'constants/pages';
import { Input } from 'components/SignInput';
import * as React from 'react';
import { useForm, Validate } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { emailAvailabilityStatus, sendValidationMail } from 'services/clients/user';
import { validateEmail } from 'services/helpers/user';
import { useSegment } from 'common/hooks/segment';

interface FormValues {
  email: string;
  override: boolean;
}

interface Props {
  proceed: (email: string) => void;
}

const PERSONAL_EMAIL_ERROR =
  "Please enter your work email or you won't be able to join your company.";
const UNAVAILABLE_EMAIL_ERROR = 'This email is already in use.';
const INVALID_DOMAIN_EMAIL_ERROR = 'This email domain is invalid. Please enter another email.';
const INVALID_EMAIL_ERROR = 'Email is not valid.';
const EMAIL_VALIDATION_ERROR = 'Could not send validation email.';

export const EmailForm: React.FC<Props> = (props) => {
  const checkEmailAvailability = useMutation(emailAvailabilityStatus);
  const sendEmailValidation = useMutation(sendValidationMail);

  const [params] = useSearchParams();

  const { trackEventNoSlack } = useSegment();

  const navigate = useNavigate();
  const { register, formState, handleSubmit, setError, trigger, getValues } = useForm<FormValues>({
    mode: 'all',
    defaultValues: { email: params.get('email') || '', override: false },
  });

  const disableSubmit = !formState.isValid;

  // Setup a manual validation function that only kicks in when nonWorkEmail is populated to handle
  // the case when we need to show the non-work-email error and override checkbox
  const [nonWorkEmail, setNonWorkEmail] = React.useState<null | string>(null);
  const checkEmail: Validate<string, FormValues> = (email) => {
    if (nonWorkEmail !== null && email === nonWorkEmail && !getValues('override')) {
      return PERSONAL_EMAIL_ERROR;
    }
    return true;
  };

  const onSubmit = handleSubmit(async (values) => {
    const { email, override } = values;
    // Local validation
    if (!validateEmail(email)) {
      setError('email', { type: 'value', message: INVALID_EMAIL_ERROR });
      return;
    }

    const continueToValidation = async (send: boolean) => {
      if (send) {
        try {
          await sendEmailValidation.mutateAsync(email);
        } catch (error) {
          setError('email', { type: 'custom', message: EMAIL_VALIDATION_ERROR });
          return;
        }
      }
      navigate(`${Routes.EmailConfirmation}?email=${encodeURIComponent(values.email)}`);
    };

    // Check if the email is already in use
    try {
      const code = await checkEmailAvailability.mutateAsync(email);
      switch (code) {
        case 'not_available':
          setError('email', { type: 'value', message: UNAVAILABLE_EMAIL_ERROR });
          trackEventNoSlack('Unavailable email submitted in sign up', { email: values.email });
          return;
        case 'not_valid':
          setError('email', {
            type: 'value',
            message: INVALID_DOMAIN_EMAIL_ERROR,
          });
          trackEventNoSlack('Invalid domain email submitted in sign up', { email: values.email });
          return;
        case 'not_work_email':
          if (override) {
            await continueToValidation(true);
            return;
          }
          setNonWorkEmail(email);
          setError('email', {
            type: 'custom',
            message: PERSONAL_EMAIL_ERROR,
          });
          trackEventNoSlack('Non-work email submitted in sign up', { email: values.email });
          return;
        case 'available':
          await continueToValidation(true);
          trackEventNoSlack('New user email submitted in sign up', { email: values.email });
          return;
        case 'needs_validation':
          await continueToValidation(false);
          trackEventNoSlack('Validation pending user email submitted in sign up', {
            email: values.email,
          });
          return;
        case 'needs_account':
          props.proceed(email);
          trackEventNoSlack('Validated email user email submitted in sign up', {
            email: values.email,
          });
          return;
        default:
          break;
      }

      if (code !== 'available') {
        setError('email', {
          type: 'value',
          message: 'An unknown response occurred while validating this email.',
        });
        return;
      }
    } catch (e: unknown) {
      setError('email', { type: 'custom', message: 'Could not determine email availbility.' });
      // eslint-disable-next-line
      console.error(e);
    }
  });

  return (
    <form onSubmit={onSubmit} className="w-full">
      <div className="space-y-8">
        <div>
          <Input
            label="Email*"
            type="email"
            placeholder="work@email.com"
            inputProps={register('email', { required: true, validate: checkEmail })}
            error={formState.errors.email}
          />
          {getValues('email') === nonWorkEmail && (
            <div className="flex items-center">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...register('override', {
                        onChange: (e) => {
                          if (e.target.checked) {
                            trigger('email');
                          }
                        },
                      })}
                    />
                  }
                  label={<h4 className="inline font-medium">Continue with personal email</h4>}
                />
              </FormGroup>
            </div>
          )}
        </div>
        <div className="space-y-4 text-center">
          <LoadingButton
            type="submit"
            size="large"
            variant="contained"
            fullWidth
            disabled={disableSubmit}
            loading={checkEmailAvailability.isLoading || sendEmailValidation.isLoading}
          >
            <h3>Continue</h3>
          </LoadingButton>
          <h4 className="text-black font-medium">
            <Link to={Routes.SignIn} className="underline">
              Sign in
            </Link>{' '}
            to your account instead.
          </h4>
        </div>
      </div>
    </form>
  );
};
EmailForm.displayName = 'SignUp.CreateEmail.EmailForm';
